import Response from "../models/response";
import { BACKEND_BASE_URL } from "../constants/contants";
import axiosInstance from "../api/axiosInstance";


export default class BaseService {

    private static baseURL: string = BACKEND_BASE_URL + "/v1";


    public static async getAll<T>(url: string): Promise<Response> {
        let res = await axiosInstance.get<Array<T>>(this.baseURL + url)
            .then((response: any) => {
                const result = response.data;
                if (response.status === 200) {
                    return new Response(true, result as Array<T>, "Success", "", response.status);
                } else {
                    const msg = (result.messageList && result.messageList.length > 0) ? result.messageList[0].text : "Error";
                    return new Response(false, null, "Error", msg, response.status);
                }
            })
            .catch(function (error) {
                return new Response(false, null, "Error", error, error.status);
            });
        return res;
    }

    public static get<T>(url: string, param: any): Promise<Response> {
        const path = param != null ? this.baseURL + url + param : this.baseURL + url;
        let res = axiosInstance.get<T>(path)
            .then((response: any) => {
                const result = response.data;
                if (response.status === 200) {
                    return new Response(true, result, "Success", "", response.status);
                } else {
                    const msg = (result.messageList && result.messageList.length > 0) ? result.messageList[0].text : "Error";
                    return new Response(false, null, "Error", msg, response.status);
                }
            })
            .catch(function (error) {
                return new Response(false, null, "Error", error, error.status);
            });
        return res;
    }

    public static delete(url: string, id: any): Promise<Response> {
        const path = id != null ? this.baseURL + url + id : this.baseURL + url;
        let res = axiosInstance.delete(path)
            .then(response => {
                const result = response.data;
                if (response.status === 200) {
                    return new Response(true, "", "Success", "", response.status);
                } else {
                    const msg = (result.messageList && result.messageList.length > 0) ? result.messageList[0].text : "Error";
                    return new Response(false, null, "Error", msg, response.status);
                }
            })
            .catch(function (error) {
                return new Response(false, null, "Error", error, error.status);
            });
        return res;
    }

    public static create<T>(url: string, obj: T): Promise<Response> {
        let res = axiosInstance.post(this.baseURL + url, obj)
            .then(response => {
                const result = response.data;
                if (response.status === 200) {
                    return new Response(true, result.data, "Success", "", response.status);
                } else {
                    const msg = (result.messageList && result.messageList.length > 0) ? result.messageList[0].text : "Error";
                    return new Response(false, null, "Error", msg, response.status);
                }
            })
            .catch(function (error) {
                return new Response(false, null, "Error", error, error.status);
            });
        return res;
    }

    public static update<T>(url: string, param: any, obj: T | null): Promise<Response> {
        const path = param != null ? this.baseURL + url + param : this.baseURL + url;
        let res = axiosInstance.put(path, obj)
            .then(response => {
                const result = response.data;
                if (response.status === 200) {
                    return new Response(true, result.data, "Success", "", response.status);
                } else {
                    const msg = (result.messageList && result.messageList.length > 0) ? result.messageList[0].text : "Error";
                    return new Response(false, null, "Error", msg, response.status);
                }
            })
            .catch(function (error) {
                return new Response(false, null, "Error", error, error.status);;
            });
        return res;
    }

    public static downloadPdf(url: string): Promise<void> {
        return axiosInstance
            .get(this.baseURL + url, { responseType: "blob" })
            .then((response: any) => {
                const result = response.data;
                if (response.status === 200) {
                    const fileName = response.headers["x-filename"] || "examination.pdf";
                    const blob = new Blob([response.data], { type: "application/pdf" });
                    const blobUrl = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = fileName; // use header name
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(blobUrl);
                }
            })
            .catch((error) => {
                console.error("Error downloading PDF", error);
            });
    }

    public static previewPdf(url: string): Promise<string> {
        return axiosInstance.get(this.baseURL + url, { responseType: "blob" })
            .then((response: any) => {
                if (response.status === 200) {
                    const blob = new Blob([response.data], { type: "application/pdf" });
                    const blobUrl = window.URL.createObjectURL(blob);
                    return blobUrl; // Return URL for iframe/modal
                } else {
                    console.error("Failed to fetch PDF for preview");
                    return "";
                }
            })
            .catch((error) => {
                console.error("Error previewing PDF", error);
                return "";
            });
    }

    public static async upload<T>(url: string, formData: FormData, extraData?: T): Promise<Response> {
        if (extraData) {
            Object.entries(extraData).forEach(([key, value]) => {
                formData.append(key, value as any);
            });
        }
        try {
            const response = await axiosInstance.post(this.baseURL + url, formData);
            const result = response.data;
            if (response.status === 200) {
                return new Response(true, result.data, "Success", "", response.status);
            } else {
                const msg =
                    result.messageList && result.messageList.length > 0
                        ? result.messageList[0].text
                        : "Error";
                return new Response(false, null, "Error", msg, response.status);
            }
        } catch (error: any) {
            return new Response(false, null, "Error", error.message || "Unknown error", error.response?.status || 500);
        }
    }

    public static async subscribeUserToPush<T>(subscription: PushSubscription): Promise<Response> {
        try {
            const keys = subscription.toJSON().keys;
            const body = {
                subscription: {
                    endpoint: subscription.endpoint,
                    keys: {
                        p256dh: keys?.p256dh,
                        auth: keys?.auth
                    }
                }
            };
            const response = await axiosInstance.post(
                this.baseURL + "/subscribe",
                body,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    transformRequest: [(data) => JSON.stringify(data)],
                }
            );

            const result = response.data;

            if (response.status === 200) {
                return new Response(true, result.data, "Success", "", response.status);
            } else {
                const msg =
                    result.messageList && result.messageList.length > 0
                        ? result.messageList[0].text
                        : "Error";
                return new Response(false, null, "Error", msg, response.status);
            }
        } catch (error: any) {
            return new Response(false, null, "Error", error.message || error, error.status);
        }
    }

    // Helper function
    public static urlBase64ToUint8Array(base64String: string) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
        const rawData = atob(base64);
        const output = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            output[i] = rawData.charCodeAt(i);
        }
        return output;
    }

}